/**
 * Printing the score, which is also how a PDF of it is made.
 *
 * A browser that can print can print to PDF, and every one of them offers that in the same
 * dialog, so there is no separate exporter here and no PDF library in the bundle: the
 * editor's job is to hand the browser pages that are the right size, and the dialog does
 * the rest.
 *
 * The music is engraved again rather than reusing what is on the desk, because the desk is
 * deliberately not paper -- it widens the music to the window and stops the last page where
 * the music stops. `PRINT_OPTIONS` puts the score back on its own page, and the render runs
 * on a spare engine so the one the editor is holding keeps the pages it drew.
 */

import { PRINT_OPTIONS, RenderError, printSheet, renderScore } from "@app/score-editor";
import type { Score } from "@app/score-editor";

import { withSpareEngine } from "./verovio";

/** An off-screen frame the size of the paper, which is what the print dialog reads. */
function frameFor(score: Score): HTMLIFrameElement {
  const frame = document.createElement("iframe");
  const { pageWidth, pageHeight } = score.layout;
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = `position:fixed;left:-10000px;top:0;border:0;width:${pageWidth}mm;height:${pageHeight}mm;`;
  return frame;
}

/** Print what the frame holds, and take the frame away whichever way the dialog ends. */
function printFrame(frame: HTMLIFrameElement): void {
  const view = frame.contentWindow;
  if (view === null) {
    frame.remove();
    return;
  }
  // Removing the frame while the dialog is open cancels the job, so it goes after the
  // dialog closes -- and `afterprint` is the only event that says so. A browser that never
  // fires it leaves one empty frame behind, which is better than a cancelled print.
  view.addEventListener("afterprint", () => frame.remove(), { once: true });
  view.focus();
  view.print();
}

/**
 * Engrave the score onto its own paper and open the browser's print dialog.
 *
 * Answers a sentence when the score could not be drawn, and `null` when the dialog was
 * opened. Nothing is printed without the user's own confirmation: the dialog is theirs.
 */
export async function printScore(score: Score): Promise<string | null> {
  let sheet: string;
  try {
    sheet = await withSpareEngine((spare) =>
      printSheet(score, renderScore(spare, score, PRINT_OPTIONS)),
    );
  } catch (raised) {
    if (raised instanceof RenderError) return "The score could not be drawn for printing.";
    throw raised;
  }

  // The sheet goes in before the frame does. A frame that enters the page empty loads a
  // blank document first and announces it, and printing on that announcement prints the
  // blank one; giving it its contents first means the only load there is is the sheet's.
  const frame = frameFor(score);
  frame.srcdoc = sheet;
  frame.addEventListener("load", () => printFrame(frame), { once: true });
  document.body.append(frame);
  return null;
}
